import * as React from 'react'
import { ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import { EmailItem, EmailType } from '../../components/List/Email/EmailList'
import { Message } from '@microsoft/microsoft-graph-types'
import { DataPromise, Destination, IGraphArrayPayload } from '../../context/AxiosContext'
import { DocumentOverviewItemDto, IDocumentOverviewItemDto, IFolderOverviewItemDto, isFolder } from '../../config/documentConfig'
import { compareDate } from '../../helpers/dateHelper'
import { IFileDto } from '../../config/fileConfig'
import useUser from '../../hooks/useUser'
import EmailToolbar from '../../components/Toolbar/Email/EmailToolbar'
import EmailSearchSelectList, { mapMessages } from '../../components/Email/EmailSearchSelectList'
import { getIntervalDate, Interval } from '../../components/Toolbar/IntervalToolbarMenu'
import { DateTime } from 'luxon'
import AuthorizationContext from '../../context/AuthorizationContext'

type Props = {
    messages: DataPromise<IGraphArrayPayload<Message>>
    documents: DataPromise<IFolderOverviewItemDto>
    file: DataPromise<IFileDto>
    reset: () => void // Callback for when the view needs to be updated.
}

const mapDocument = (document: IDocumentOverviewItemDto): EmailItem => ({
    id: document.id,
    internetMessageId: undefined,
    conversationId: undefined,
    destination: Destination.Documents,
    type: document.name.indexOf('-IN-') !== -1 ? EmailType.In : EmailType.Out,
    sender: undefined,
    recipient: document.name.replace(/-(IN|UIT)-/, '-').replace(/\.(msg|eml)$/, ''),
    recipients: [],
    subject: '',
    snippet: '',
    date: document.date,
    hasAttachment: false,
    url: document.url,
})

const mapDocuments = (documents: DocumentOverviewItemDto[]): EmailItem[] =>
    documents
        .filter(item => isFolder(item) || item.name.match(/\.(msg|eml)$/))
        .flatMap(item => (isFolder(item) ? mapDocuments(item.children || []) : [mapDocument(item)]))

export default function EmailOverview(props: Props): ReactElement {
    const { currentEmail } = useUser()
    const [interval, setInterval] = useState<Interval>(Interval.All)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const { host: currentHost } = useContext(AuthorizationContext)

    const originalMessages: EmailItem[] = useMemo<EmailItem[]>(() => {
        if (props.messages.loaded && (props.documents.loaded || props.documents.rejected)) {
            const dateInterval = getIntervalDate(interval)
            return [
                ...mapMessages(
                    props.messages.data?.value || [],
                    currentHost?.ReplicationMail,
                    currentEmail,
                    currentHost,
                    Destination.ReplicationEmail,
                ),
                ...mapDocuments(props.documents.data?.children || []),
            ]
                .filter(message => {
                    return dateInterval === undefined || DateTime.fromISO(message.date).diff(dateInterval).toMillis() > 0
                })
                .sort((a: EmailItem, b: EmailItem) => compareDate(b.date, a.date))
        }
        return []
    }, [props.messages.data, props.documents.data, interval])

    useEffect(() => {
        setIsLoading(props.file?.loading || props.messages?.loading || props.documents?.loading)
    }, [props.file.loading, props.messages.loading, props.documents.loading])

    return (
        <EmailSearchSelectList
            emailMailbox={currentHost?.ReplicationMail}
            interval={interval}
            setInterval={setInterval}
            messages={props.messages}
            items={originalMessages}
            hasItems={originalMessages?.length !== 0}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            reset={props.reset}
            showLocation={true}
            file={props.file}
            toolbar={EmailToolbar}
            searchable={true}
        />
    )
}
